import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { Network } from '../types/models/Network';
import type { ApiResponseNetworkList } from '../types/api/ApiResponseNetworks';

interface UseNetworksOptions {
  skip?: boolean;
}

interface UseNetworksReturn {
  networks: Network[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useNetworks = (options?: UseNetworksOptions): UseNetworksReturn => {
  const skip = options?.skip ?? false;
  const [networks, setNetworks] = useState<Network[]>([]);
  const [loading, setLoading] = useState<boolean>(!skip);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchNetworks = useCallback(async () => {
    if (skip) {
      setNetworks([]);
      setLoading(false);
      setError(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/datacenters/networks');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseNetworkList;
      if (data.status === 'success' && data.data?.networks) {
        setNetworks(data.data.networks);
      } else if (data.status === 'fail' || data.status === 'error') {
        setError(data.message ?? 'Failed to load networks');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching networks');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, skip]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return;
    }
    if (skip) {
      setNetworks([]);
      setLoading(false);
      setError(null);
      return;
    }
    fetchNetworks();
  }, [auth.isAuthenticated, fetchNetworks, skip]);

  return { networks, loading, error, refetch: fetchNetworks };
};
